/** Form controls. Every control can carry a `help` key shown as a hover tooltip. */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { fmt } from '../core/units';
import { Icon } from './icons';

export function Field({ label, help, children, wide }: { label: string; help?: string; children: ComponentChildren; wide?: boolean }) {
  return (
    <label class={`field${wide ? ' wide' : ''}`} data-help={help}>
      <span class="field-label">{label}</span>
      <span class="field-control">{children}</span>
    </label>
  );
}

interface NumberProps {
  label?: string;
  value: number | null;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  help?: string;
  unit?: string;
  digits?: number;
  disabled?: boolean;
  integer?: boolean;
  placeholder?: string;
  testId?: string;
  allowInfinity?: boolean;
  /** Commit every valid keystroke (for dialogs), not only on Enter / blur. */
  live?: boolean;
}

export function parseNum(s: string): number {
  const t = s.trim().replace(/−/g, '-').replace(',', '.');
  if (/^[+]?(inf|infinity|∞)$/i.test(t)) return Infinity;
  if (t === '') return NaN;
  return Number(t);
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  help,
  unit,
  digits = 6,
  disabled,
  integer,
  placeholder,
  testId,
  allowInfinity,
  live,
}: NumberProps) {
  const show = (v: number | null) =>
    v === null || v === undefined ? '' : !Number.isFinite(v) ? (v > 0 ? '∞' : '') : fmt(v, digits).replace(/−/g, '-');
  const [text, setText] = useState(show(value));
  const [focus, setFocus] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    // Check real DOM focus: the focus state can lag, and a late sync would overwrite text being typed.
    if (!focus && document.activeElement !== input.current) setText(show(value));
  }, [value, focus]);
  const commit = (t: string) => {
    let v = parseNum(t);
    if (!Number.isFinite(v) && !(allowInfinity && v === Infinity)) {
      setBad(t.trim() !== '');
      return;
    }
    if (integer && Number.isFinite(v)) v = Math.round(v);
    if (min !== undefined) v = Math.max(min, v);
    if (max !== undefined) v = Math.min(max, v);
    setBad(false);
    onChange(v);
    setText(show(v));
  };
  return (
    <span class="num" data-help={help}>
      <input
        ref={input}
        type="text"
        inputMode="decimal"
        class={bad ? 'bad' : ''}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        onFocus={() => setFocus(true)}
        onBlur={(e) => {
          setFocus(false);
          commit((e.target as HTMLInputElement).value);
        }}
        onInput={(e) => {
          const t = (e.target as HTMLInputElement).value;
          setText(t);
          if (live) {
            const v = parseNum(t);
            if (Number.isFinite(v) && (min === undefined || v >= min) && (max === undefined || v <= max))
              onChange(integer ? Math.round(v) : v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit((e.target as HTMLInputElement).value);
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setText(show(value));
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const cur = parseNum(text);
            const base = Number.isFinite(cur) ? cur : (value ?? 0);
            const k = (e.shiftKey ? 10 : e.altKey ? 0.1 : 1) * step * (e.key === 'ArrowUp' ? 1 : -1);
            commit(String(Number((base + k).toPrecision(12))));
          }
        }}
      />
      {unit && <span class="unit">{unit}</span>}
    </span>
  );
}

export function NumberField(p: NumberProps & { label: string }) {
  return (
    <Field label={p.label} help={p.help}>
      <NumberInput {...p} help={undefined} />
    </Field>
  );
}

export interface Opt<T> {
  value: T;
  label: string;
  disabled?: boolean;
}

export function Select<T extends string | number>({
  value,
  options,
  onChange,
  help,
  testId,
  disabled,
}: {
  value: T;
  options: Opt<T>[];
  onChange: (v: T) => void;
  help?: string;
  testId?: string;
  disabled?: boolean;
}) {
  return (
    <select
      data-help={help}
      data-testid={testId}
      disabled={disabled}
      value={String(value)}
      onChange={(e) => {
        const raw = (e.target as HTMLSelectElement).value;
        const o = options.find((x) => String(x.value) === raw);
        if (o) onChange(o.value);
      }}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function SelectField<T extends string | number>(p: {
  label: string;
  value: T;
  options: Opt<T>[];
  onChange: (v: T) => void;
  help?: string;
  testId?: string;
  disabled?: boolean;
}) {
  return (
    <Field label={p.label} help={p.help}>
      <Select {...p} help={undefined} />
    </Field>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  help,
  testId,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  help?: string;
  testId?: string;
  disabled?: boolean;
}) {
  return (
    <label class={`toggle${disabled ? ' disabled' : ''}`} data-help={help} data-testid={testId}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange((e.target as HTMLInputElement).checked)} />
      <span class="toggle-box" />
      <span>{label}</span>
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  help,
  testId,
}: {
  value: T;
  options: Opt<T>[];
  onChange: (v: T) => void;
  help?: string;
  testId?: string;
}) {
  return (
    <div class="segmented" data-help={help} data-testid={testId} role="radiogroup">
      {options.map((o) => (
        <button
          type="button"
          key={o.value}
          role="radio"
          aria-checked={o.value === value}
          class={o.value === value ? 'on' : ''}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Button({
  children,
  onClick,
  help,
  kind = 'default',
  disabled,
  icon,
  testId,
  active,
  small,
}: {
  children?: ComponentChildren;
  onClick?: (e: MouseEvent) => void;
  help?: string;
  kind?: 'default' | 'primary' | 'ghost' | 'danger';
  disabled?: boolean;
  icon?: string;
  testId?: string;
  active?: boolean;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      class={`btn ${kind}${active ? ' active' : ''}${small ? ' small' : ''}${children ? '' : ' icon-only'}`}
      data-help={help}
      data-testid={testId}
      disabled={disabled}
      aria-pressed={active === undefined ? undefined : active}
      onClick={onClick}
    >
      {icon && <Icon name={icon} size={small ? 14 : 16} />}
      {children && <span>{children}</span>}
    </button>
  );
}

export function Section({
  title,
  children,
  defaultOpen = true,
  help,
  right,
}: {
  title: string;
  children: ComponentChildren;
  defaultOpen?: boolean;
  help?: string;
  right?: ComponentChildren;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div class={`section${open ? ' open' : ''}`}>
      <div class="section-head" data-help={help}>
        <button type="button" class="section-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
          <span>{title}</span>
        </button>
        {right && <div class="section-right">{right}</div>}
      </div>
      {open && <div class="section-body">{children}</div>}
    </div>
  );
}

/** Two-column key / value table. */
export function KV({ rows, testId }: { rows: [string, string, string?][]; testId?: string }) {
  return (
    <table class="kv" data-testid={testId}>
      <tbody>
        {rows.map(([k, v, h]) => (
          <tr key={k} data-help={h}>
            <th>{k}</th>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Hint({ children, kind = 'info' }: { children: ComponentChildren; kind?: 'info' | 'warn' | 'error' }) {
  return <div class={`hint ${kind}`}>{children}</div>;
}
